import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, Output, ViewChild, OnInit, ViewEncapsulation } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatTabsModule } from "@angular/material/tabs";
import { AssetManagementService } from "@app/assetmanagement/services/assetmanagement-data.service";
import { CerrixHoverCardDirective } from "@app/shared/cerrix-hover-card/cerrix-hover-card.directve";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { CerrixIconButtonComponent } from "@app/shared/molecules/cerrix-icon-button/cerrix-icon-button.component";
import { CerrixAddHyperlinkDialogComponent } from "@app/shared/organisms/cerrix-add-hyperlink-dialog/cerrix-add-hyperlink-dialog.component";
import { CerrixConfirmDialogComponent } from "@app/shared/organisms/cerrix-confirm-dialog/cerrix-confirm-dialog.component";
import { CerrixDialogService } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog-service.component";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { AssetHyperlinkModel } from "@models/assetmanagement/assethyperlinkmodel";
import { AssetManagementModel } from "@models/assetmanagement/assetmanagementmodel";
import { finalize, firstValueFrom } from "rxjs";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { ToastrService } from "ngx-toastr";
import { OpenAddHyperlinkDialogReason } from "@app/shared/organisms/cerrix-add-hyperlink-dialog/models/open-dialog-reason-model";
import { CerrixKebabMenuComponent, CerrixKebabMenuItem } from "@app/shared/organisms/cerrix-kebab-menu/cerrix-kebab-menu.component";

@Component({
    selector: "app-asset-hyperlinks",
    templateUrl: "./asset-hyperlinks.component.html",
    styleUrls: ["./asset-hyperlinks.component.scss"],
    imports: [
        CommonModule,
        CerrixIconButtonComponent,
        TranslocoPipe,
        MatTabsModule,
        CerrixInstructionsIconComponent,
        MatMenuModule,
        MatIconModule,
        CerrixHoverCardDirective,
        CerrixConfirmDialogComponent,
        CerrixAddHyperlinkDialogComponent,
        CerrixKebabMenuComponent
    ],
    providers: [],
    encapsulation: ViewEncapsulation.None,
})
export class AssetHyperlinksComponent implements OnInit {
    @Input() model: AssetManagementModel;
    @Input() canEdit: boolean = true;
    @Output() hyperlinksChanged: EventEmitter<AssetManagementModel> = new EventEmitter<AssetManagementModel>();
    @ViewChild(CerrixAddHyperlinkDialogComponent) hyperlinkDialog: CerrixAddHyperlinkDialogComponent;

    private _ds = inject(AssetManagementService);
    private _transloco = inject(TranslocoService);
    private _dialogService = inject(CerrixDialogService);
    private _promptService = inject(CerrixPromptService);
    private _toastService = inject(ToastrService);

    protected openHyperlinkDialogReason: OpenAddHyperlinkDialogReason;
    protected openHyperlinkDialogName = "addHyperlinkDialog";
    protected confirmRemoveHyperlinkDialogName = "confirmRemoveHyperlinkDialog";
    protected saveHyperlinkLoading = false;
    protected removeHyperlinkLoading = false;
    protected dontAskMeAgainSelected: boolean = false;
    private _hyperlinkToRemove: AssetHyperlinkModel | null = null;

    async ngOnInit(): Promise<void> {
        await firstValueFrom(this._transloco.load(`assetmanagement/${this._transloco.getActiveLang()}`));

        this.dontAskMeAgainSelected = localStorage.getItem("dontAskAgainRemoveHyperlink") === "true";
    }

    public async onRemoveHyperlink(hyperlink: AssetHyperlinkModel): Promise<void> {
        this.removeHyperlinkLoading = true;

        const savingPrompt = this._promptService.loader(
            this._transloco.translate("assetmanagement.hyperlinks.removingHyperlink")
        )

        const storedAsset = await firstValueFrom(
            this._ds.deleteAssetHyperlinkVbeta(
                hyperlink.id,
                { assetId: this.model.guid, version: this.model.version }
            ).pipe(
                finalize(() => {
                    savingPrompt.close();
                    this.removeHyperlinkLoading = false;
                })
            )
        );

        if (storedAsset?.guid) {
            this.hyperlinksChanged.emit(Object.assign(new AssetManagementModel(), storedAsset));

            this._toastService.success(
                this._transloco.translate("assetmanagement.hyperlinks.removeCompleted")
            )
        }
    }

    public async onAddHyperlink(): Promise<void> {
        this.openHyperlinkDialog(null, OpenAddHyperlinkDialogReason.Add);
    }

      // The URL validator accepts scheme-less input (e.g. "google.com"), so an unmodified href
    // is treated by the browser as a relative path. Prepend https:// when no scheme is present.
    protected getHyperlinkHref(url: string): string {
        if (this.isValidURI(url)){
            return url;
        } else{
            return `https://${url}`;        
        }
    }


    protected isValidURI(uri: string): boolean {
        try {
            new URL(uri);
            return true;
        } catch {
            return false;
        }
    }

    public async onViewHyperlink(hyperlink: AssetHyperlinkModel): Promise<void> {
        if(this.canEdit){
            this.openHyperlinkDialog(hyperlink, OpenAddHyperlinkDialogReason.Edit);
        }else{
            this.openHyperlinkDialog(hyperlink, OpenAddHyperlinkDialogReason.View);
        }
    }

    public async saveHyperlink(result: AssetHyperlinkModel): Promise<void> {
        this._dialogService.closeDialog(this.openHyperlinkDialogName);

        this.saveHyperlinkLoading = true;
        const savingPrompt = this._promptService.loader(
            this._transloco.translate("assetmanagement.hyperlinks.savingHyperlink")
        )

        let storedAsset = null;

        if (this.openHyperlinkDialogReason === OpenAddHyperlinkDialogReason.Add) {
            storedAsset = await firstValueFrom(
                this._ds.attachAssetHyperlinkVbeta(
                    this.model.guid,
                    {
                        name: result.name,
                        hyperlinkUrl: result.hyperlinkUrl,
                        hyperlinkType: result.hyperlinkType,
                        description: result.description,
                        version: this.model.version
                    }
                ).pipe(
                    finalize(() => {
                        savingPrompt.close();
                        this.saveHyperlinkLoading = false;
                    })
                )
            );
        }
        else if (this.openHyperlinkDialogReason === OpenAddHyperlinkDialogReason.Edit) {
            storedAsset = await firstValueFrom(
                this._ds.editAssetHyperlinkVbeta(
                    result.id,
                    {
                        assetId: this.model.guid,
                        name: result.name,
                        hyperlinkUrl: result.hyperlinkUrl,
                        hyperlinkType: result.hyperlinkType,
                        description: result.description,
                        version: this.model.version
                    }
                ).pipe(
                    finalize(() => {
                        savingPrompt.close();
                        this.saveHyperlinkLoading = false;
                    })
                )
            );
        }

        if (storedAsset?.guid) {
            this.hyperlinksChanged.emit(Object.assign(new AssetManagementModel(), storedAsset));

            this._toastService.success(
                this._transloco.translate("assetmanagement.hyperlinks.saveCompleted")
            )
        }
    }
    
    public closeCancel(): void {
        this._dialogService.closeDialog(this.openHyperlinkDialogName);
    }

    protected removeHyperlinkCancel() {
        this._hyperlinkToRemove = null;
        this.dontAskMeAgainSelected = localStorage.getItem("dontAskAgainRemoveHyperlink") === "true";
        this._dialogService.closeDialog(this.confirmRemoveHyperlinkDialogName);
    }

    protected async removeHyperlinkConfirm() {
        if (this._hyperlinkToRemove) {
            await this.onRemoveHyperlink(this._hyperlinkToRemove);
        }
        localStorage.setItem("dontAskAgainRemoveHyperlink", this.dontAskMeAgainSelected.toString());
        this._hyperlinkToRemove = null;
        this._dialogService.closeDialog(this.confirmRemoveHyperlinkDialogName);
    }

    protected async openRemoveHyperlinkDialog(hyperlink: AssetHyperlinkModel): Promise<void> {
        this._hyperlinkToRemove = hyperlink;

        if (localStorage.getItem("dontAskAgainRemoveHyperlink") === "true") {
            await this.removeHyperlinkConfirm();
        } else {
            this.dontAskMeAgainSelected = localStorage.getItem("dontAskAgainRemoveHyperlink") === "true";
            this._dialogService.openDialog(this.confirmRemoveHyperlinkDialogName);
        }
    }

    private openHyperlinkDialog(hyperlink: AssetHyperlinkModel | null, reason: OpenAddHyperlinkDialogReason): void {
        this.openHyperlinkDialogReason = reason;
        this.hyperlinkDialog.setDialogData(hyperlink, reason);
        this._dialogService.openDialog(this.openHyperlinkDialogName);
    }

    public getKebabItems(hyperlink: AssetHyperlinkModel): CerrixKebabMenuItem[] {
        const MenuItems = [];
        if(hyperlink){
            MenuItems.push(this.getViewKebabItem(hyperlink));
            if (this.canEdit) {
                MenuItems.push(this.getDeleteKebabItem(hyperlink));
            }
        }
        return MenuItems;
    }

    private getViewKebabItem(hyperlink: AssetHyperlinkModel): CerrixKebabMenuItem {
        return {
            icon: "instructions",
            text: this._transloco.translate("assetmanagement.hyperlinks.informationHyperlinkButton"),
            onClick: () => {
                this.onViewHyperlink(hyperlink);
            }
        };
    }

    private getDeleteKebabItem(hyperlink: AssetHyperlinkModel): CerrixKebabMenuItem {
        return {
            icon: "delete",
            text: this._transloco.translate("assetmanagement.hyperlinks.deleteHyperlinkButton"),
            onClick: () => {
                this.openRemoveHyperlinkDialog(hyperlink);
            }
        };
    }
}
